import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/Services/api.service';
import { StorageService } from 'src/app/Services/storage.service';

declare const google: any;

@Component({
    selector: 'g10-request-response-view',
    templateUrl: './request-response-view.component.html',
    styleUrls: ['./request-response-view.component.css']
})
export class RequestResponseViewComponent implements OnInit {

    public requestTypeParam = '';
    public requestID = '';
    public appRequestID = '';
    public backRoute = '/organization/transport-manager/request/list-request';
    public titleName = 'View Request';

    public requestData: any = {};
    public responseData: any = {};
    public formPreviewFields: any[] = [];
    public isLoaded = false;
    public isMapPopupOpen = false;
    public selectedLocationLabel = '';
    public mapError = '';
    public branchInformation: any = {};

    constructor(
        private route: ActivatedRoute,
        private api: ApiService,
        private storage: StorageService,
    ) {
        this.route.paramMap.subscribe((params: any) => {
            this.requestTypeParam = params.params.requestType;
            this.requestID = params.params.requestID;
            this.appRequestID = params.params.appRequestID;
            this.backRoute = `/organization/transport-manager/request/list-request/${this.requestTypeParam}/${this.requestID}`;
        });
    }

    ngOnInit(): void {
        this.loadBranchList();
        this.loadRequestView();
    }

    loadBranchList() {
        const orgID = this.storage.get('userInfo')?.orgID;
        if (!orgID) return;

        this.api.transportManagerBranchList({ orgID }).then((res: any) => {
            if (res?.status === 'success' && Array.isArray(res.data)) {
                this.branchInformation = {};
                for (let i = 0; i < res.data.length; i++) {
                    const branch = res.data[i];
                    if (branch?.branchID) {
                        this.branchInformation[branch.branchID] = branch.branchName || branch.branchID;
                    }
                }
            }
        }).catch((err) => {
            console.log(err);
        });
    }

    loadRequestView() {
        this.isLoaded = false;
        const reqType = this.requestTypeParam.split('-').join('_');
        this.api.ListRequest({ requestID: this.requestID }).then((res: any) => {
            this.requestData = res?.data?.[reqType]?.[0] ? res.data[reqType][0] : {};
            if (this.requestData?.title) {
                this.titleName = this.requestData.title;
            } else {
                this.titleName = this.requestTypeParam.split('-').join(' ');
            }
            this.loadResponseData();
        }).catch((err) => {
            console.log(err);
            this.loadResponseData();
        });
    }

    loadResponseData() {
        this.api.RequestResList({ requestID: this.requestID }).then((res: any) => {
            const responseList = Array.isArray(res?.data) ? res.data : [];
            this.responseData = responseList.find((item: any) => item.appRequestID == this.appRequestID) || {};
            this.preparePreviewFields();
            this.isLoaded = true;
        }).catch((err) => {
            console.log(err);
            this.isLoaded = true;
        });
    }

    preparePreviewFields() {
        const fields = Array.isArray(this.requestData?.formFields) ? this.requestData.formFields : [];
        this.formPreviewFields = fields
        .filter((field: any) => this.isRenderablePreviewField(field))
        .map((field: any) => {
            const clonedField = { ...field };
            const fieldValue = this.getResponseValueForField(clonedField);

            if (['date', 'multipleDate'].includes(clonedField.type)) {
                clonedField.value = this.formatDateFieldValue(fieldValue);
            } else {
                clonedField.value = fieldValue ?? '';
            }

            return clonedField;
        });
    }

    getFieldClass(field: any) {
        return 'col-md-3 col-sm-6 mb-3';
    }

    isLocationField(field: any) {
        if (!field) return false;
        return field.type === 'map' || ['location', 'newLocation', 'currentLocation'].includes(field.name);
    }

    shouldShowMapAction(field: any) {
        return this.isLocationField(field) && this.getFieldValue(field) !== '---';
    }

    getFieldTooltip(field: any) {
        const value = this.getFieldValue(field);
        return value !== '---' ? value : '';
    }

    getFieldIcon(fieldOrLabel: any) {
        const rawValue = typeof fieldOrLabel === 'string'
            ? fieldOrLabel
            : (fieldOrLabel?.label || fieldOrLabel?.name || '');
        const key = String(rawValue).trim().toLowerCase();

        if (key.includes('employee name') || key === 'empname') return 'person';
        if (key.includes('employee id') || key === 'emprefid') return 'badge';
        if (key.includes('submitted') || key.includes('action time') || key.includes('effective date') || key.includes('trip date') || key === 'date') return 'event';
        if (key.includes('status')) return 'info';
        if (key.includes('location')) return 'place';
        if (key.includes('address')) return 'home';
        if (key.includes('area')) return 'map';
        if (key.includes('city')) return 'location_city';
        if (key.includes('branch')) return 'account_tree';
        if (key.includes('zone')) return 'category';
        if (key.includes('nodal')) return 'share_location';
        if (key.includes('pincode')) return 'pin_drop';
        if (key.includes('message') || key.includes('note')) return 'chat_bubble_outline';
        if (key.includes('shift')) return 'schedule';
        if (key.includes('transit mode')) return 'swap_horiz';
        if (key.includes('route')) return 'alt_route';
        if (key.includes('time')) return 'access_time';

        return 'label';
    }

    openFieldLocationMap(field: any) {
        if (!field) return;
        const locationSource = field.value ?? this.getResponseValueForField(field);
        this.openLocationMap(locationSource);
    }

    getFieldValue(field: any) {
        if (!field) return '---';
        const value = field.value;

        if (value === null || value === undefined || value === '') return '---';

        if (field?.name === 'branch') {
            const branchValue = this.getBranchName(value);
            return this.shouldTitleCaseFieldKey('branch') ? this.convertTitleCaseString(this.getDisplayValue(branchValue)) : this.getDisplayValue(branchValue);
        }

        if (Array.isArray(value)) {
            const mapped = value
                .map((item) => {
                    if (item === null || item === undefined || item === '') return null;
                    if (typeof item === 'object') return item.name || item.value || JSON.stringify(item);
                    return String(item);
                })
                .filter(Boolean);
            const joined = mapped.length > 0 ? mapped.join(', ') : '---';
            return this.shouldTitleCaseFieldKey(field?.name) ? this.convertTitleCaseString(joined) : joined;
        }

        if (typeof value === 'object') {
            let result = '---';
            if (value.name) result = value.name;
            else if (value.lat !== undefined && value.lng !== undefined) result = `${value.lat}, ${value.lng}`;
            else result = Object.values(value).filter(Boolean).join(', ') || '---';
            return this.shouldTitleCaseFieldKey(field?.name) ? this.convertTitleCaseString(result) : result;
        }

        if (typeof value === 'boolean') return value ? 'Yes' : 'No';

        const stringValue = String(value);
        return this.shouldTitleCaseFieldKey(field?.name) ? this.convertTitleCaseString(stringValue) : stringValue;
    }

    getStatusLabel(status: any) {
        const parsedStatus = +status;
        if (parsedStatus === 2) return 'In-progress';
        if (parsedStatus === 3) return 'Completed';
        if (parsedStatus === 4) return 'Rejected';
        return 'Pending';
    }

    getDisplayValue(value: any) {
        return value !== null && value !== undefined && value !== '' ? value : '---';
    }

    private convertTitleCaseString(value: string): string {
        if (value === null || value === undefined || value === '') return '';
        return value
            .split(' ')
            .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '')
            .join(' ');
    }

    private shouldTitleCaseFieldKey(key: string): boolean {
        if (!key) return false;
        const keys = [
            'area',
            'city',
            'zone',
            'zoneName',
            'zone_name',
            'nodal',
            'nodalPoint',
            'nodalPoints',
            'message',
            'note',
            'address',
            'location',
            'locationName',
            'branch'
        ];
        return keys.includes(key);
    }

    getRouteBranchValue() {
        const value = this.getFormattedFieldValue(
            this.responseData?.branchName ??
            this.getBranchName(this.responseData?.branch)
        );
        return this.shouldTitleCaseFieldKey('branch') ? this.convertTitleCaseString(value) : value;
    }

    getRouteNodalValue() {
        const value = this.responseData?.nodalPoint ?? this.responseData?.nodalPoints ?? this.responseData?.nodal;
        const formatted = this.getFormattedFieldValue(value);
        return this.shouldTitleCaseFieldKey('nodal') ? this.convertTitleCaseString(formatted) : formatted;
    }

    isRouteLocationChangeRequest() {
        return this.requestTypeParam === 'route-location-change-request';
    }

    getRouteLocationValue() {
        const locationSource =
            this.responseData?.newLocation ??
            this.responseData?.location ??
            this.responseData?.currentLocation;

        const value = this.getLocationLabel(locationSource);
        const formatted = this.getDisplayValue(value);
        return this.shouldTitleCaseFieldKey('location') ? this.convertTitleCaseString(formatted) : formatted;
    }

    getRouteAreaValue() {
        const value = this.getDisplayValue(this.responseData?.area);
        return this.shouldTitleCaseFieldKey('area') ? this.convertTitleCaseString(value) : value;
    }

    private getFormattedFieldValue(value: any) {
        return this.getDisplayValue(this.normalizeFieldValue(value));
    }

    private isRenderablePreviewField(field: any): boolean {
        return !!(field && field.type && (field.name || field.label));
    }

    private getResponseValueForField(field: any): any {
        const fieldName = field?.name;
        if (!fieldName || !this.responseData) return '';

        if (this.responseData[fieldName] !== undefined) {
            return this.responseData[fieldName];
        }

        if (fieldName === 'shift') {
            return this.responseData?.pickup ?? this.responseData?.drop ?? this.responseData?.shift ?? '';
        }

        if (fieldName === 'transitMode') {
            if (this.responseData?.pickup) return 'Login';
            if (this.responseData?.drop) return 'Logout';
        }

        return '';
    }

    private formatDateFieldValue(value: any): any {
        if (value === null || value === undefined || value === '') return '';

        if (Array.isArray(value)) {
            const formattedDates = value
                .map((item) => this.formatSingleDateValue(item))
                .filter((item) => item !== '');
            return formattedDates.join(', ');
        }

        return this.formatSingleDateValue(value);
    }

    private formatSingleDateValue(value: any): string {
        const date = new Date(value);
        return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB');
    }

    private getBranchName(id: any) {
        if (!id) return '';
        return this.branchInformation[id] || id;
    }

    private normalizeFieldValue(value: any): any {
        if (value === null || value === undefined || value === '') return '';

        if (Array.isArray(value)) {
            const normalized = value
                .map((item) => this.normalizeFieldValue(item))
                .filter((item) => item !== null && item !== undefined && item !== '');
            return normalized.length ? normalized.join(', ') : '';
        }

        if (typeof value === 'object') {
            return value.name || value.label || value.branchName || value.nodalName || value.value || '';
        }

        return value;
    }

    openLocationMap(locationSource?: any) {
        locationSource =
            locationSource ??
            this.responseData?.newLocation ??
            this.responseData?.location ??
            this.responseData?.currentLocation;

        this.selectedLocationLabel = this.getLocationLabel(locationSource);
        this.mapError = '';
        this.isMapPopupOpen = true;

        setTimeout(async () => {
            try {
                const coordinates = await this.resolveLocationCoordinates(locationSource);
                if (!coordinates) {
                    this.mapError = 'Location coordinates are not available for this request.';
                    return;
                }

                this.renderLocationMap(coordinates.lat, coordinates.lng, this.selectedLocationLabel);
            } catch (error) {
                console.log(error);
                this.mapError = 'Unable to load the selected location on the map.';
            }
        }, 0);
    }

    closeLocationMap() {
        this.isMapPopupOpen = false;
        this.mapError = '';
    }

    private getLocationLabel(location: any) {
        if (location === null || location === undefined || location === '') return '---';
        if (typeof location === 'string') return location;
        if (typeof location === 'object') {
            return location.address || location.name || location.label || location.area || location.formattedAddress || '---';
        }
        return String(location);
    }

    private getCoordinateValue(location: any, keys: string[]) {
        if (!location || typeof location !== 'object') return undefined;

        for (const key of keys) {
            if (location[key] !== undefined && location[key] !== null && location[key] !== '') {
                return Number(location[key]);
            }
        }

        return undefined;
    }

    private async resolveLocationCoordinates(location: any): Promise<{ lat: number, lng: number } | null> {
        if (location === null || location === undefined || location === '') return null;

        if (typeof location === 'object') {
            const lat = this.getCoordinateValue(location, ['lat', 'latitude']);
            const lng = this.getCoordinateValue(location, ['lng', 'lon', 'long', 'longitude']);

            if (!isNaN(lat) && !isNaN(lng)) {
                return { lat, lng };
            }

            if (location.location) {
                return this.resolveLocationCoordinates(location.location);
            }

            if (location.coordinates) {
                return this.resolveLocationCoordinates(location.coordinates);
            }

            const objectLabel = this.getLocationLabel(location);
            return objectLabel !== '---' ? this.geocodeLocation(objectLabel) : null;
        }

        if (typeof location === 'string') {
            const parts = location.split(',').map((item: string) => item.trim());
            if (parts.length === 2) {
                const lat = Number(parts[0]);
                const lng = Number(parts[1]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    return { lat, lng };
                }
            }

            return this.geocodeLocation(location);
        }

        return null;
    }

    private geocodeLocation(address: string): Promise<{ lat: number, lng: number } | null> {
        return new Promise((resolve) => {
            if (!address || typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
                resolve(null);
                return;
            }

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address }, (results: any, status: string) => {
                if (status === 'OK' && results?.length) {
                    const loc = results[0].geometry.location;
                    resolve({ lat: loc.lat(), lng: loc.lng() });
                    return;
                }
                resolve(null);
            });
        });
    }

    private renderLocationMap(lat: number, lng: number, title: string) {
        if (typeof google === 'undefined' || !google.maps) {
            this.mapError = 'Google Maps is not available.';
            return;
        }

        const mapElement = document.getElementById('requestLocationMap');
        if (!mapElement) {
            this.mapError = 'Map container is not available.';
            return;
        }

        const position = new google.maps.LatLng(lat, lng);
        const map = new google.maps.Map(mapElement, {
            center: position,
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
        });

        new google.maps.Marker({
            position,
            map,
            title
        });
    }
}
